import { fetchClubBySlug, type FetchClubBySlugResult } from './api'
import { hasStorefront } from './storefront'
import {
  forgetStorefrontVisit,
  rememberStorefrontVisit,
  storefrontReturnToConfirm,
} from './storefrontReturn'
import { storefrontSlugFor } from '@/app/router/resolveChrome'

/**
 * ASK THE SERVER ONCE WHETHER THE CLUB THE TAB REMEMBERS STILL EXISTS.
 *
 * After a reload the header draws the remembered club from the tab's note
 * (`./storefrontReturn.ts`), so nothing flashes. That note may be stale: the club
 * could have been unpublished, lost its public slug, or been renamed since this tab
 * stood on it. One public read settles it. A live answer refreshes the store, and the
 * store always wins over the note. "Not found" or "moved" forgets the club, and the bar
 * goes back to our wordmark. A network failure or a server error proves nothing either
 * way, so the note is left alone.
 *
 * At most one read per page load, and none when the club's own storefront is on screen.
 * That page reads the same club itself, and a second request would only duplicate it.
 */
let asked = false

export function settleRememberedClub(result: FetchClubBySlugResult): void {
  if (result.ok) {
    const club = result.details.club
    if (club && hasStorefront(club) && club.name) rememberStorefrontVisit(club.slug)
    else forgetStorefrontVisit()
    return
  }
  if ('redirectSlug' in result || result.status === 404) forgetStorefrontVisit()
}

export async function confirmRememberedClub(pathname: string): Promise<void> {
  const slug = storefrontReturnToConfirm()
  if (asked || !slug || storefrontSlugFor(pathname)) return
  asked = true
  try {
    settleRememberedClub(await fetchClubBySlug(slug))
  } catch {
    // An unexpected failure is not evidence the club is gone; keep the note.
  }
}
